import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import {
  Currency,
  currenciesConfig,
  WithdrawalType,
  routePaths,
} from "../../../constants";
import { useTransaction } from "../../../contexts/TransactionContext";
import {
  ratesResourcePath,
  useGetExpressRates,
} from "../../../services/queries/rates";
import useDebounce from "../../../hooks/useDebounce";
import { useFilters } from "../../../hooks/useFilters";
import { payoutRules } from "../../../utils/wallet";
import {
  calculateQuoteAmount,
  calculateBaseAmount,
  calculateGrossAmount,
} from "../../../utils/currency";
import { CurrencyExchangeInput } from "../CurrencyExchangeInput";
import { ExchangeRateDisplay } from "../ExchangeRateDisplay";
import { TransactionBreakdown } from "../../TransactionBreakdown";
import CurrencySelectorBottomSheet from "../CurrencySelectorBottomSheet";

function getPayoutAmount(baseAmount: string, baseCurrency: Currency) {
  const minimumAmount =
    payoutRules.minimumAmounts[WithdrawalType.INTERNATIONAL][
      baseCurrency as keyof (typeof payoutRules.minimumAmounts)[WithdrawalType.INTERNATIONAL]
    ];
  return Number(baseAmount) > minimumAmount ? baseAmount : minimumAmount;
}

function InternationalWithdrawalForm() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [activeInput, setActiveInput] = useState<"base" | "quote">("base");
  const [isCurrencySelectorOpen, setIsCurrencySelectorOpen] = useState(false);
  const [currencySelectorMode, setCurrencySelectorMode] = useState<
    "base" | "quote"
  >("quote");

  const { state: formData, updateTransaction } = useTransaction();
  const { baseCurrency, baseAmount, quoteCurrency, remarks, balance } =
    formData;

  const paymentMethod = currenciesConfig.find(
    (item) => item.currency === quoteCurrency,
  )?.paymentMethods[0];

  // Debounce the base amount for rate fetching (reduced from 1000ms to 500ms)
  const debouncedBaseAmount = useDebounce(baseAmount, 500);

  // Get express exchange rates with auto-refresh
  // Use a default minimum amount for rate display when no amount is entered
  const payoutAmount = getPayoutAmount(
    debouncedBaseAmount || "1",
    baseCurrency,
  );
  const currencyPair = `${baseCurrency}/${quoteCurrency}`;
  const { buildApiUrl } = useFilters({
    currencyPair,
    amount: payoutAmount,
    paymentMethod: paymentMethod?.value,
  });

  const url = buildApiUrl(`${ratesResourcePath}`);

  const {
    data,
    isLoading: isLoadingRates,
    refetch,
    dataUpdatedAt,
  } = useGetExpressRates(url, {
    refetchInterval: 900000, // Auto-refresh every 15 mins
    staleTime: 1800000, // Consider stale after 30 mins
    enabled: true, // Always fetch rate to show exchange rate even without amount
  });

  const exchangeRate = data?.data;
  const rateId = exchangeRate?.id;
  const rate = exchangeRate?.finalRate;
  const fee = exchangeRate?.fee ?? 0;
  const minimumAmount = exchangeRate?.minimumAmount as number;
  const maximumAmount = exchangeRate?.maximumAmount as number;

  // Calculate quote amount when base changes
  const calculatedQuoteAmount = useMemo(() => {
    if (!rate || activeInput !== "base") return "";
    if (!debouncedBaseAmount) return ""; // Clear quote if base is empty
    return calculateQuoteAmount(debouncedBaseAmount, rate, fee);
  }, [debouncedBaseAmount, rate, fee, activeInput]);

  // Calculate base amount when quote changes
  const calculatedBaseAmount = useMemo(() => {
    if (!rate || activeInput !== "quote") return "";
    if (!formData.quoteAmount) return ""; // Clear base if quote is empty
    return calculateBaseAmount(formData.quoteAmount, rate, fee);
  }, [formData.quoteAmount, rate, fee, activeInput]);

  // Update quote amount when base changes
  useEffect(() => {
    if (activeInput === "base") {
      updateTransaction({
        quoteAmount: calculatedQuoteAmount,
      });
    }
  }, [calculatedQuoteAmount, activeInput, updateTransaction]);

  // Update base amount when quote changes
  useEffect(() => {
    if (activeInput === "quote") {
      updateTransaction({
        baseAmount: calculatedBaseAmount,
      });
    }
  }, [calculatedBaseAmount, activeInput, updateTransaction]);

  // Validation
  useEffect(() => {
    setError("");

    if (!baseAmount) return;

    if (Number(baseAmount) > balance) {
      setError(
        `Amount exceeds your balance of ${balance.toLocaleString(
          "en-US",
        )} ${baseCurrency}`,
      );
      return;
    }

    if (
      minimumAmount !== undefined &&
      minimumAmount > 0 &&
      Number(baseAmount) < minimumAmount
    ) {
      setError(
        `Minimum amount is ${minimumAmount.toLocaleString(
          "en-US",
        )} ${baseCurrency}`,
      );
      return;
    }

    if (
      maximumAmount !== undefined &&
      maximumAmount > 0 &&
      Number(baseAmount) > maximumAmount
    ) {
      setError(
        `Maximum amount is ${maximumAmount.toLocaleString(
          "en-US",
        )} ${baseCurrency}`,
      );
      return;
    }
  }, [baseAmount, balance, minimumAmount, maximumAmount, baseCurrency]);

  const handleBaseAmountChange = (amount: string) => {
    setActiveInput("base");
    updateTransaction({
      baseAmount: amount,
    });
  };

  const handleQuoteAmountChange = (amount: string) => {
    setActiveInput("quote");
    updateTransaction({
      quoteAmount: amount,
    });
  };

  const handleRemarksChange = (remarks: string) => {
    updateTransaction({
      remarks,
    });
  };

  const handleBaseCurrencyClick = () => {
    // Check if base currency can send to other currencies
    const baseConfig = currenciesConfig.find(
      (config) => config.currency === baseCurrency,
    );
    if (baseConfig && baseConfig.canSend.length > 0) {
      setCurrencySelectorMode("base");
      setIsCurrencySelectorOpen(true);
    }
  };

  const handleQuoteCurrencyClick = () => {
    setCurrencySelectorMode("quote");
    setIsCurrencySelectorOpen(true);
  };

  const handleCurrencySelect = (selectedCurrency: Currency) => {
    if (currencySelectorMode === "base") {
      //change base currency
      updateTransaction({
        baseCurrency: selectedCurrency,
        baseAmount: "",
        quoteAmount: "",
        // Reset quote currency to first available in the new base currency's canSend list
        quoteCurrency:
          currenciesConfig.find((c) => c.currency === selectedCurrency)
            ?.canSend[0]?.value || quoteCurrency,
      });
    } else {
      // Change quote currency - keep baseAmount to trigger new rate fetch, but clear quoteAmount
      updateTransaction({
        quoteCurrency: selectedCurrency,
        quoteAmount: "", // Clear quote amount, will be recalculated with new rate
      });
    }
  };

  const handleSubmit = () => {
    updateTransaction({
      rateId,
      fee,
    });

    navigate({ to: routePaths.beneficiary });
  };

  const grossAmount = useMemo(() => {
    if (!rate) return 0;
    return calculateGrossAmount(baseAmount, rate);
  }, [baseAmount, rate]);

  const isInValidWithdrawalForm =
    !baseAmount || !formData.quoteAmount || isLoadingRates || error !== "";

  // Check if base currency can be changed
  const canChangeBaseCurrency = useMemo(() => {
    const baseConfig = currenciesConfig.find(
      (config) => config.currency === baseCurrency,
    );
    return baseConfig ? baseConfig.canSend.length > 0 : false;
  }, [baseCurrency]);

  // Check if quote currency can be changed
  const canChangeQuoteCurrency = useMemo(() => {
    const baseConfig = currenciesConfig.find(
      (config) => config.currency === baseCurrency,
    );
    return baseConfig ? baseConfig.canSend.length > 0 : false;
  }, [baseCurrency]);

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-6 flex-1 overflow-y-auto pb-4">
        {/* Currency Exchange Input with Dual Inputs */}
        <CurrencyExchangeInput
          baseCurrency={baseCurrency}
          quoteCurrency={quoteCurrency}
          baseAmount={baseAmount}
          quoteAmount={formData.quoteAmount}
          exchangeRate={rate ?? null}
          fee={fee}
          isLoading={isLoadingRates}
          onBaseAmountChange={handleBaseAmountChange}
          onQuoteAmountChange={handleQuoteAmountChange}
          onBaseCurrencyClick={handleBaseCurrencyClick}
          onQuoteCurrencyClick={handleQuoteCurrencyClick}
          canChangeBaseCurrency={canChangeBaseCurrency}
          canChangeQuoteCurrency={canChangeQuoteCurrency}
          error={error}
          minAmount={minimumAmount}
          maxAmount={maximumAmount}
          balance={balance}
          activeInput={activeInput}
        />

        {/* Exchange Rate Display with Freshness Indicator */}
        {exchangeRate && baseCurrency && quoteCurrency && (
          <ExchangeRateDisplay
            baseCurrency={baseCurrency}
            quoteCurrency={quoteCurrency}
            rate={rate ?? 0}
            fetchedAt={new Date(dataUpdatedAt)}
            isRefreshing={isLoadingRates}
            onRefresh={() => refetch()}
          />
        )}

        {/* Transaction Breakdown */}
        {exchangeRate &&
          baseAmount &&
          formData.quoteAmount &&
          baseCurrency &&
          quoteCurrency && (
            <TransactionBreakdown
              baseAmount={baseAmount}
              baseCurrency={baseCurrency}
              quoteAmount={formData.quoteAmount}
              quoteCurrency={quoteCurrency}
              rate={rate ?? 0}
              fee={fee}
              grossAmount={grossAmount}
            />
          )}

        {/* Remarks Input */}
        <div className="bg-white rounded-lg p-4 relative border border-gray-200">
          <input
            type="text"
            placeholder="Add Note"
            value={remarks}
            onChange={(e) => handleRemarksChange(e.target.value)}
            className="w-full focus:outline-none text-gray-500"
            maxLength={18}
          />

          <div className="text-right text-xs text-gray-400 absolute bottom-2 right-2">
            {remarks?.length}/18
          </div>
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 left-0 right-0 bg-white pt-4 pb-6 md:pb-4">
          <button
            disabled={isInValidWithdrawalForm}
            className={`w-full bg-primary text-white py-4 px-6 rounded-full font-medium ${
              isInValidWithdrawalForm
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-primary/90"
            }`}
            onClick={handleSubmit}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Currency Selector Bottom Sheet */}
      <CurrencySelectorBottomSheet
        isOpen={isCurrencySelectorOpen}
        onClose={() => setIsCurrencySelectorOpen(false)}
        selectedCurrency={
          currencySelectorMode === "base" ? baseCurrency : quoteCurrency
        }
        onSelectCurrency={handleCurrencySelect}
        mode={currencySelectorMode}
        baseCurrency={baseCurrency}
      />
    </div>
  );
}

export default React.memo(InternationalWithdrawalForm);
